import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/shared/hooks/useTheme";
import { normalize } from "@/i18n";
import {
  installHostContextListener,
  notifyHostReady,
} from "@/shared/lib/host-lifecycle";

/**
 * Whether this document has said studio:ready. Module-level, not per mount
 * (prod, 12 Oct): the host answers every ready with studio:context, so a
 * bridge that re-announced on each remount turned any remount into another
 * round of context, and a language switch that remounted the tree into a loop
 * that doubled every round and froze the dashboard tab. One document, one
 * ready.
 */
let announcedReady = false;

/** Test seam: forget the announcement, as a fresh document would. */
export function resetHostReadyForTest(): void {
  announcedReady = false;
}

/** Applies the host's studio:context (theme + locale) to the running app. */
export function HostContextBridge() {
  const { setTheme } = useAppTheme();
  const { i18n } = useTranslation();

  useEffect(() => {
    const uninstall = installHostContextListener({
      onTheme: (theme) => setTheme(theme),
      // Only a real change: re-applying the language already showing must not
      // set anything in motion.
      onLocale: (locale) => {
        const next = normalize(locale);
        if (next !== normalize(i18n.language)) void i18n.changeLanguage(next);
      },
    });
    // ORDER IS THE POINT: install first, then announce. Announcing before the
    // listener exists would re-create the exact race this closes — the host
    // answers immediately, and the reply would land on nothing.
    if (!announcedReady) {
      announcedReady = true;
      notifyHostReady();
    }
    return uninstall;
  }, [setTheme, i18n]);

  return null;
}
